/**
 * useVirtualizedMarks - Viewport-aware mark filtering for stress overlay
 *
 * Why this exists: The StressOverlayPlugin renders marks for ALL syllables in
 * the document, even those scrolled way off-screen. For long lyrics with many
 * marks, this wastes React reconciliation cycles and DOM operations. This hook
 * filters to only visible marks (plus a buffer for smooth scrolling).
 *
 * How it works:
 *   1. Observes scroll/resize events on the container
 *   2. Calculates visible viewport bounds
 *   3. Filters marks to those within viewport + buffer
 *   4. Returns filtered marks for rendering
 *
 * The full mark list is retained (not recomputed) - we just filter for render.
 *
 * Test/jsdom behavior:
 *   When the container has zero dimensions (common in jsdom), returns all marks
 *   to avoid filtering everything out. This is the expected fallback.
 */

import { useState, useEffect, useCallback, useRef, useMemo, type RefObject } from 'react'

export interface VirtualizableMark {
  /** Unique key for React */
  key: string
  /** X position relative to container */
  x: number
  /** Y position relative to container */
  y: number
}

interface ViewportBounds {
  top: number
  bottom: number
  left: number
  right: number
}

/**
 * Filter marks to only those visible in viewport (plus buffer)
 *
 * @param allMarks - Complete array of marks (kept as cache, not recomputed)
 * @param containerRef - Ref to the scrollable container element
 * @param buffer - Extra pixels beyond viewport to include (default 100)
 * @returns Filtered array of visible marks
 */
export function useVirtualizedMarks<T extends VirtualizableMark>(
  allMarks: T[],
  containerRef: RefObject<HTMLElement | null>,
  buffer: number = 100
): T[] {
  const [visibleMarks, setVisibleMarks] = useState<T[]>([])
  const frameIdRef = useRef<number | null>(null)
  const lastBoundsRef = useRef<ViewportBounds | null>(null)

  // Calculate visible marks based on current viewport
  const updateVisibleMarks = useCallback(() => {
    const container = containerRef.current
    if (!container) {
      // No container - fall back to showing all marks
      setVisibleMarks(allMarks)
      return
    }

    // Get container's bounding rect for viewport bounds
    const rect = container.getBoundingClientRect()

    // Fallback: If container has no measurable dimensions (common in jsdom/tests),
    // show all marks to avoid filtering everything out
    if (rect.height <= 0 || rect.width <= 0) {
      setVisibleMarks(allMarks)
      return
    }

    // For the overlay, marks have positions relative to the editor root,
    // not the viewport. So we need to calculate the visible region
    // in the coordinate system the marks use.
    const scrollTop = container.scrollTop || 0
    const scrollLeft = container.scrollLeft || 0

    const bounds: ViewportBounds = {
      top: scrollTop - buffer,
      bottom: scrollTop + rect.height + buffer,
      left: scrollLeft - buffer,
      right: scrollLeft + rect.width + buffer,
    }

    // Quick optimization: if bounds haven't changed much, skip update
    const last = lastBoundsRef.current
    if (last) {
      const boundsDelta = 20
      if (
        Math.abs(bounds.top - last.top) < boundsDelta &&
        Math.abs(bounds.bottom - last.bottom) < boundsDelta &&
        Math.abs(bounds.left - last.left) < boundsDelta &&
        Math.abs(bounds.right - last.right) < boundsDelta
      ) {
        return
      }
    }
    lastBoundsRef.current = bounds

    // Filter marks to those within viewport + buffer
    const filtered = allMarks.filter((mark) => {
      return mark.y >= bounds.top && mark.y <= bounds.bottom
    })

    setVisibleMarks(filtered)
  }, [allMarks, containerRef, buffer])

  // Throttled scroll/resize handler using RAF
  const handleScrollOrResize = useCallback(() => {
    if (frameIdRef.current !== null) {
      return
    }
    frameIdRef.current = requestAnimationFrame(() => {
      frameIdRef.current = null
      updateVisibleMarks()
    })
  }, [updateVisibleMarks])

  // Set up scroll and resize listeners
  useEffect(() => {
    const container = containerRef.current
    if (!container) {
      // No container yet - show all marks
      setVisibleMarks(allMarks)
      return
    }

    // Initial calculation
    updateVisibleMarks()

    // Listen for scroll events on the container
    container.addEventListener('scroll', handleScrollOrResize, { passive: true })

    // Also listen for resize
    const resizeObserver = new ResizeObserver(handleScrollOrResize)
    resizeObserver.observe(container)

    return () => {
      container.removeEventListener('scroll', handleScrollOrResize)
      resizeObserver.disconnect()
      if (frameIdRef.current !== null) {
        cancelAnimationFrame(frameIdRef.current)
        frameIdRef.current = null
      }
    }
  }, [containerRef, handleScrollOrResize, updateVisibleMarks, allMarks])

  // When allMarks changes, recalculate visible set
  useEffect(() => {
    updateVisibleMarks()
  }, [allMarks, updateVisibleMarks])

  // Memoized early return for the jsdom/test case:
  // If container doesn't exist or has zero dimensions, return allMarks directly
  // This ensures tests see marks without waiting for async state updates
  const result = useMemo(() => {
    const container = containerRef.current
    if (!container) {
      return allMarks
    }
    const rect = container.getBoundingClientRect()
    if (rect.height <= 0 || rect.width <= 0) {
      return allMarks
    }
    // Real browser case - return filtered marks from state
    return visibleMarks.length > 0 ? visibleMarks : allMarks
  }, [containerRef, allMarks, visibleMarks])

  return result
}

export default useVirtualizedMarks
